import type { MapLocation } from "@/Shared/Maps/MapLocation";
import type { LatitudeLongitude } from "@freightsimple/generated-dashboard-openapi-client/models/LatitudeLongitude";

type LocationWithValidCoordinates = Partial<MapLocation> & {
  latitudeLongitude: Required<LatitudeLongitude>;
};

// A location's coordinates are only usable on the map when both values are
// present, finite, inside the valid geographic range, and not the (0,0)
// sentinel. Upstream geocoding can leave coordinates missing or stored as a
// literal (0,0); plotting that lands a point at 0°N 0°E in the Gulf of Guinea,
// which is what surfaced as a bogus "Canada to Africa" route on the booking
// review map (ENG-4342). Treating those as "no coordinates" lets the map skip
// the bad point — and its route line — while still showing any valid location.
//
// Returns a type guard so callers can use the coordinates without re-asserting.
export function hasValidLatitudeLongitude(
  location: Partial<MapLocation> | undefined,
): location is LocationWithValidCoordinates {
  const latitudeLongitude = location?.latitudeLongitude;
  if (latitudeLongitude === undefined) {
    return false;
  }

  const { latitude, longitude } = latitudeLongitude;

  if (
    latitude === undefined ||
    longitude === undefined ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude)
  ) {
    return false;
  }

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return false;
  }

  // (0,0) is open ocean — never a real freight location, and the tell-tale of a
  // failed geocode being persisted as zero rather than left empty.
  if (latitude === 0 && longitude === 0) {
    return false;
  }

  return true;
}
